import type { ContaConectada, ConviteRecebido } from "../types";

/** Cores do Google: texto forte sobre tom pastel, o mesmo vocabulário dos relatórios. */
export const CORES_KPI = {
  verde: { fundo: "#EAF7EE", cor: "#188038", borda: "#A8DAB5" },
  vermelha: { fundo: "#FCE8E6", cor: "#D93025", borda: "#F6AEA9" },
  amarela: { fundo: "#FFF7E0", cor: "#E37400", borda: "#FDD663" },
  azul: { fundo: "#EAF1FB", cor: "#1264C7", borda: "#C2D7F5" },
  roxa: { fundo: "#F3E8FD", cor: "#7B1FA2", borda: "#D7AEFB" },
  cinza: { fundo: "#F1F3F4", cor: "#5F6368", borda: "#DADCE0" },
} as const;


export type FiltroDeConta = "TODAS" | "RECONECTAR" | "COM_CONVITES" | "SEM_ACESSO" | "SEM_CONEXAO";

export const FILTROS_DE_CONTA: { chave: FiltroDeConta; rotulo: string }[] = [
  { chave: "TODAS", rotulo: "Todas" },
  { chave: "RECONECTAR", rotulo: "Precisam reconectar" },
  { chave: "COM_CONVITES", rotulo: "Com convites pendentes" },
  { chave: "SEM_ACESSO", rotulo: "Com perfis sem acesso" },
  { chave: "SEM_CONEXAO", rotulo: "Nunca conectadas" },
];

export const problemasDaConta = (conta: ContaConectada): number =>
  conta.semAcesso + conta.suspensos + conta.copias;

/** Precisa reconectar = tinha token e o Google passou a recusá-lo. Conta que nunca conectou é outra coisa. */
export const precisaReconectar = (conta: ContaConectada): boolean => conta.hasToken && conta.needsReauthentication;

export const nuncaConectada = (conta: ContaConectada): boolean => !conta.hasToken;

/** Os quatro números do topo: o que a equipe precisa olhar primeiro. */
export const resumoDasContas = (contas: ContaConectada[]) => ({
  conectadas: contas.filter((conta) => conta.connected).length,
  reconectar: contas.filter(precisaReconectar).length,
  semToken: contas.filter(nuncaConectada).length,
  convites: contas.reduce((total, conta) => total + conta.invitationsPending, 0),
  perfis: contas.reduce((total, conta) => total + conta.profiles, 0),
  comProblema: contas.reduce((total, conta) => total + problemasDaConta(conta), 0),
});

const aceita = (conta: ContaConectada, filtro: FiltroDeConta): boolean => {
  switch (filtro) {
    case "RECONECTAR":
      return precisaReconectar(conta);
    case "COM_CONVITES":
      return conta.invitationsPending > 0;
    case "SEM_ACESSO":
      return conta.semAcesso > 0;
    case "SEM_CONEXAO":
      return nuncaConectada(conta);
    default:
      // "Todas" esconde o ruído: conta que nunca conectou e não administra perfil.
      return conta.hasToken || conta.profiles > 0;
  }
};

export const filtrarContas = (
  contas: ContaConectada[],
  filtro: FiltroDeConta,
  busca: string,
): ContaConectada[] => {
  const termo = busca.trim().toLowerCase();
  return contas.filter(
    (conta) =>
      (termo ? true : aceita(conta, filtro)) &&
      (!termo ||
        (conta.email ?? "").toLowerCase().includes(termo) ||
        (conta.name ?? "").toLowerCase().includes(termo)),
  );
};

/** Situação da conexão em palavras de quem opera, não em flags. */
export const situacaoDaConta = (
  conta: ContaConectada,
): { rotulo: string; cor: "verde" | "vermelha" | "cinza" } => {
  if (conta.connected) return { rotulo: "Conectada", cor: "verde" };
  if (precisaReconectar(conta)) return { rotulo: "Precisa reconectar", cor: "vermelha" };
  return { rotulo: "Nunca conectada", cor: "cinza" };
};

/** Resumo dos perfis da conta que não mente quando nada foi verificado ainda. */
export const resumoDosPerfis = (conta: ContaConectada): { texto: string; alerta: boolean } => {
  const problemas = problemasDaConta(conta);
  if (conta.profiles === 0) return { texto: "nenhum perfil administrado", alerta: false };
  if (problemas > 0) return { texto: `${problemas} com problema`, alerta: true };
  if (conta.comAcesso === conta.profiles) return { texto: "todos com acesso", alerta: false };
  if (conta.naoVerificados > 0) return { texto: `${conta.naoVerificados} ainda não verificados`, alerta: false };
  if (conta.acessoSolicitado > 0) return { texto: `${conta.acessoSolicitado} com acesso solicitado`, alerta: false };
  return { texto: `${conta.comAcesso} com acesso`, alerta: false };
};

/** A mensagem crua do Google (JSON de 400 caracteres) não ajuda quem opera; traduz o que importa. */
export const explicarErro = (erro: string | null | undefined): string | null => {
  if (!erro) return null;
  const texto = erro.toLowerCase();
  if (texto.includes("401") || texto.includes("invalid credentials") || texto.includes("invalid_grant")) {
    return "O Google recusou o token desta conta. Reconecte pelo menu Conectar Email.";
  }
  if (texto.includes("403") || texto.includes("permission")) {
    return "O Google negou permissão para listar as contas deste e-mail.";
  }
  if (texto.includes("sem token")) return "Conta ainda não conectada ao Google.";
  if (texto.includes("429") || texto.includes("quota")) return "Limite de consultas do Google atingido; tente mais tarde.";
  const primeiraLinha = erro.split("\n")[0].replace(/\s+/g, " ").trim();
  return primeiraLinha.length > 110 ? `${primeiraLinha.slice(0, 109).trimEnd()}…` : primeiraLinha;
};

const PAPEIS: Record<string, string> = {
  OWNER: "proprietário",
  PRIMARY_OWNER: "proprietário principal",
  CO_OWNER: "coproprietário",
  MANAGER: "gerente",
  SITE_MANAGER: "gerente do site",
};

export const descricaoDoConvite = (convite: ConviteRecebido): string => {
  const alvo = convite.targetName?.trim() || "uma ficha";
  const papel = convite.role ? (PAPEIS[convite.role] ?? convite.role.toLowerCase()) : null;
  const tipo = convite.targetType === "ACCOUNT" ? "a conta" : "a ficha";
  return `Convite para gerenciar ${tipo} “${alvo}”${papel ? ` como ${papel}` : ""}`;
};

export const iniciais = (texto: string | null | undefined, reserva: string): string => {
  const base = (texto ?? "").trim() || reserva;
  const partes = base.replace(/@.*$/, "").split(/[\s._-]+/).filter(Boolean);
  return partes
    .slice(0, 2)
    .map((parte) => parte[0]?.toUpperCase() ?? "")
    .join("") || "?";
};
